import React, { ReactNode } from 'react'
import { connect, DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { autobind } from 'core-decorators'
import { ITableProperty, IWorkStep, CrudPage, WorkStepPage, emptyPage } from '../../models'
import { IStoreState, ISessionState } from '../../store/states'
import { Menu } from '../../containers'
import {
  Button,
  Panel,
  Table,
  Toggle,
  Field,
  Pagination,
  TableActions,
  DraftWorkStepModal,
  PageHeader,
  Input,
  DeleteConfirmationModal
} from '../../components'
import { WorkStepService } from '../../services'
import { setWorkStepsAction } from '../../store/actions'

interface IProps {
  session: ISessionState
  page: WorkStepPage
}

type Props = IProps & DispatchProp<AnyAction>

// tslint:disable:jsx-no-lambda

class WorkStepsPage extends CrudPage<IWorkStep, Props> {
  private tableProperties: ITableProperty[] = [
    { key: 'name', label: 'workStep' },
    { key: 'operation', label: 'operation', className: 'no-flex' }
  ]

  private get tableItems(): Array<{}> {
    const items = this.props.page.items
    return items && items.map(x => ({
      name: x.name,
      operation:
        <TableActions onDelete={ () => this.handleOpenDelete(x) } onEdit={ () => this.handleOpenEdit(x) } />
    }))
  }

  constructor(props) {
    super(props)
    this.state = this.initialState
  }

  public componentWillUnmount(): void {
    super.componentWillUnmount()
    this.props.dispatch(setWorkStepsAction(emptyPage))
  }

  public render(): ReactNode {
    const {
      isCreating,
      isEditing,
      isSendingRequest,
      editingItem,
      areArchivedIncluded,
      filters,
      deletingItem
    } = this.state
    const { page, session } = this.props
    return (
      <section className="page fully-aligned work-steps">
        <Menu />
        <article className="content">
          <PageHeader session={ session } label="workSteps" />
          <div className="filters">
            <Button label="new" className="new-button" onClick={ this.handleOpenCreate } />
            <Input
              value={ filters.query.value }
              placeholder="search"
              isInvalid={ !!filters.query.value && !filters.query.isValid }
              onChange={ x => this.handleFilterChange({ query: { value: x, isValid: x.length >= 3 } }) }
            />
          </div>
          <Panel>
            <Table properties={ this.tableProperties } items={ this.tableItems } />
          </Panel>
          <footer>
            <Field label="includeArchieved" >
              <Toggle
                isActive={ areArchivedIncluded }
                onChange={ this.handleArchivedIncludedChange }
              />
            </Field>
            <Pagination
              onClick={ this.fetchItems }
              isHidden={ !page.pagination }
              pagination={ page.pagination }
            />
          </footer>
        </article>
        <DraftWorkStepModal
            title={ isCreating ? 'create' : 'save' }
            isHidden={ !isCreating && !isEditing }
            onSubmit={ (isCreating && this.handleCreate) || (isEditing && this.handleEdit) }
            onCancel={ (isCreating && this.handleCancelCreate) || (isEditing && this.handleCancelEdit) }
            item={ editingItem }
            isLoading={ isSendingRequest }
          />
        <DeleteConfirmationModal
          isHidden={ !deletingItem }
          onCancel={ () => this.handleOpenDelete(null) }
          onConfirm={ this.handleDelete }
          isLoading={ isSendingRequest }
        />
      </section>
    )
  }

  @autobind
  protected async fetchItems(page = 1, areArchivedIncluded = false): Promise<void> {
    try {
      const query = this.state.filters.query
      const result = await WorkStepService.getWorkSteps(page, areArchivedIncluded, query)
      this.props.dispatch(setWorkStepsAction(result))
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    }
  }

  @autobind
  protected async handleDelete(): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      await WorkStepService.deleteWorkStep(this.state.deletingItem.id)
      this.setState({ deletingItem: null })
      await this.fetchItems()
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }

  @autobind
  protected async handleCreate(workStep: IWorkStep): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      await WorkStepService.createWorkStep(workStep)
      await this.fetchItems()
      this.setState({ isCreating: false })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }

  @autobind
  protected async handleEdit(workStep: IWorkStep): Promise<void> {
    try {
      this.setState({ isSendingRequest: true })
      await WorkStepService.updateWorkStep(workStep)
      await this.fetchItems()
      this.setState({ isEditing: false, editingItem: null })
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
    } finally {
      this.setState({ isSendingRequest: false })
    }
  }
}

const mapStateToProps = ({ session, workSteps }: IStoreState): IProps => ({
  session,
  page: workSteps
})

export default connect(mapStateToProps)(WorkStepsPage)
